import React, { createContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';

export const AuthContext = createContext();

const AUTH_ME_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const AUTH_ME_CACHE_KEYS = {
  token: 'authMe:token',
  at: 'authMe:at',
  user: 'authMe:user'
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const INACTIVITY_LIMIT_MS = 10 * 60 * 1000; // 10 minutes

  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const scheduleInactivityTimer = () => {
    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(() => {
      // Auto-logout on inactivity
      logout('inactive');
    }, INACTIVITY_LIMIT_MS);
  };

  const markActivity = () => {
    lastActivityRef.current = Date.now();
    try { localStorage.setItem('lastActivityAt', String(lastActivityRef.current)); } catch {}
    scheduleInactivityTimer();
  };

  const addActivityListeners = () => {
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(evt => window.addEventListener(evt, markActivity, { passive: true }));
    // Reset when tab becomes visible again
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) markActivity();
    });
  };

  const removeActivityListeners = () => {
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(evt => window.removeEventListener(evt, markActivity));
    document.removeEventListener('visibilitychange', () => {});
  };

  const broadcastLogout = (reason = 'manual') => {
    try { localStorage.setItem('logout', JSON.stringify({ at: Date.now(), reason })); } catch {}
  };

  const logout = (reason = 'manual') => {
    try { localStorage.removeItem('token'); } catch {}
    try {
      sessionStorage.removeItem(AUTH_ME_CACHE_KEYS.token);
      sessionStorage.removeItem(AUTH_ME_CACHE_KEYS.at);
      sessionStorage.removeItem(AUTH_ME_CACHE_KEYS.user);
    } catch {}
    broadcastLogout(reason);
    clearInactivityTimer();
    removeActivityListeners();
    setUser(null);
    // Optional hard redirect to login route if app expects it
    // window.location.href = '/login';
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    // Fast path: reuse recent /me response to avoid repeated calls on refresh
    try {
      const cachedToken = sessionStorage.getItem(AUTH_ME_CACHE_KEYS.token);
      const cachedAt = Number(sessionStorage.getItem(AUTH_ME_CACHE_KEYS.at) || 0);
      const cachedUserRaw = sessionStorage.getItem(AUTH_ME_CACHE_KEYS.user);
      const fresh = cachedToken === token && cachedAt && (Date.now() - cachedAt) < AUTH_ME_CACHE_TTL_MS;
      if (fresh && cachedUserRaw) {
        try {
          const cachedUser = JSON.parse(cachedUserRaw);
          if (cachedUser && typeof cachedUser === 'object') {
            setUser(cachedUser);
            setLoading(false);
            // Still initialize inactivity + listeners
            markActivity();
            addActivityListeners();
          }
        } catch {}
      }
    } catch {}

    axios.get(apiUrl('/api/auth/me'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        setUser(res.data);
        try {
          sessionStorage.setItem(AUTH_ME_CACHE_KEYS.token, token);
          sessionStorage.setItem(AUTH_ME_CACHE_KEYS.at, String(Date.now()));
          sessionStorage.setItem(AUTH_ME_CACHE_KEYS.user, JSON.stringify(res.data));
        } catch {}
        // Start inactivity tracking only when authenticated
        markActivity();
        addActivityListeners();
        // Cross-tab logout listener
        const onStorage = (e) => {
          if (e.key === 'logout') {
            setUser(null);
            clearInactivityTimer();
          }
          if (e.key === 'token' && e.newValue == null) {
            setUser(null);
            clearInactivityTimer();
          }
        };
        window.addEventListener('storage', onStorage);
        // Cleanup on unmount
        return () => {
          window.removeEventListener('storage', onStorage);
        };
      })
      .catch(() => {
        try { localStorage.removeItem('token'); } catch {}
        try {
          sessionStorage.removeItem(AUTH_ME_CACHE_KEYS.token);
          sessionStorage.removeItem(AUTH_ME_CACHE_KEYS.at);
          sessionStorage.removeItem(AUTH_ME_CACHE_KEYS.user);
        } catch {}
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Note: We intentionally do NOT auto-logout on beforeunload to avoid logging out on page reload.

  if (loading) return <div>Loading...</div>;

  return <AuthContext.Provider value={{ user, setUser, logout }}>{children}</AuthContext.Provider>;
};

export default AuthProvider;